/**
 * New pricing section — single source of truth for product data.
 * Transcribed from IQ-Capital-Pricing-Spec-for-IT.md ("build exactly to this
 * document"). Never invent or round a number here beyond the spec's own
 * `round(price × 0.2)` discount rule.
 */

export type Asset = "futures" | "cfd_crypto";
export type ProductId = "core" | "rapid" | "instant";
export type SizeId = "50k" | "100k" | "200k";

/** Loss-rule variants (Futures Core / Instant Funding) and Rapid funded variants. */
export type VariantId = "single" | "mpl" | "dll" | "classic" | "daily";

/** Platform families; selection FILTERS what is shown (spec §2.2). */
export type PlatformId = "dxfeed" | "tradovate" | "mt5" | "volumetrica";

export const PLATFORMS_BY_ASSET: Record<Asset, PlatformId[]> = {
  futures: ["dxfeed", "tradovate"],
  cfd_crypto: ["mt5", "volumetrica"],
};

/**
 * Which Rapid funded variants are shown (spec §2.4 config requirement):
 * "both" | "classic" | "daily".
 *
 * Launch state: Daily only — Classic follows in the next few days.
 * TO REVERT: change back to "both".
 */
export const RAPID_FUNDED_VARIANTS: "both" | "classic" | "daily" = "daily";

/** Spec §2.7 — −80% on every headline price; fees are never discounted. */
export const DISCOUNT_PERCENT = 80;
export const discounted = (priceUsd: number) => Math.round(priceUsd * 0.2);

/** Spec §2.8 — every product/variant. */
export const DRAWDOWN_MODE = "EOD Trailing";

/** Spec §5 — checkout link builder. */
export const checkoutUrl = (presel: string) =>
  `https://checkout.iqcapital.io/products?presel=${presel}`;

/** All numbers optional: a blank Sanity field renders "—" (no invented defaults). */
export interface ChallengeData {
  priceUsd?: number;
  profitTargetUsd?: number;
  maxDrawdownUsd?: number;
  /** exactly one of the two loss rules — or neither (Futures Rapid challenge) */
  maxPositionLossPct?: number;
  dailyLossLimitUsd?: number;
  consistencyPct: number | null;
  durationDays?: number;
  extensionFeeUsd?: number;
}

export interface FundedData {
  /** undefined = the row is hidden for this product (e.g. Futures Rapid funded) */
  activationFeeUsd?: number | "free";
  /** trader share, e.g. 90 = 90/10 */
  profitSplitTrader?: number;
  payoutEveryDays?: number;
  maxDrawdownUsd?: number;
  maxPositionLossPct?: number;
  dailyLossLimitUsd?: number;
  consistencyPct: number | null;
  /** null = "—" (no reset offered) */
  resetFeeUsd: number | null;
  /** undefined = the row is hidden for this product (e.g. Futures Rapid funded) */
  extensionFeeUsd?: number;
}

/** Marketing-added extra KPI row (Sanity-driven, V2-style flexibility). */
export interface CustomSpec {
  /** stable identity for pairing rows across columns */
  id: string;
  /** known KPI key when the editor picked one (label/tooltip come from copy) */
  kpiKey?: string;
  label: string;
  value: string;
}

/** One purchasable column (a sub-column in the matrix). */
export interface ChallengeCol {
  variant: VariantId;
  presel: string;
  /** Optional platform restriction (Sanity-driven); absent = default rule (§2.2). */
  platforms?: string[];
  customSpecs?: CustomSpec[];
  data: ChallengeData;
}
export interface FundedCol {
  variant: VariantId;
  /** Same challenge link for Core/Rapid funded views; Instant has its own. */
  presel: string;
  /** Instant Funding headline price lives on the funded column. */
  priceUsd?: number;
  /** Optional platform restriction (Sanity-driven); absent = default rule (§2.2). */
  platforms?: string[];
  customSpecs?: CustomSpec[];
  data: FundedData;
}

export interface SizeEntry {
  size: SizeId;
  /** CFD-only optional tier display names (Advanced/Expert/Master) */
  tierName?: string;
  /** Platforms this size is available on */
  platforms: PlatformId[];
  challenge: ChallengeCol[];
  funded: FundedCol[];
}

export interface Product {
  id: ProductId;
  maxAccounts?: number;
  sizes: SizeEntry[];
}

export type Catalog = Record<Asset, Record<ProductId, Product>>;

/* ── §4.1 CFD + Crypto ─────────────────────────────────────────────────── */

const CFD_CORE: Product = {
  id: "core",
  maxAccounts: 10,
  sizes: [
    {
      size: "50k",
      tierName: "Advanced",
      platforms: ["mt5", "volumetrica"],
      challenge: [
        {
          variant: "single",
          presel: "advanced-50k",
          data: {
            priceUsd: 199,
            profitTargetUsd: 4000,
            maxDrawdownUsd: 3000,
            maxPositionLossPct: 1,
            consistencyPct: null,
            durationDays: 30,
            extensionFeeUsd: 5,
          },
        },
      ],
      funded: [
        {
          variant: "single",
          presel: "advanced-50k",
          data: {
            activationFeeUsd: 129,
            profitSplitTrader: 90,
            payoutEveryDays: 10,
            maxDrawdownUsd: 3000,
            maxPositionLossPct: 0.5,
            consistencyPct: 30,
            resetFeeUsd: 299,
            extensionFeeUsd: 5,
          },
        },
      ],
    },
    {
      size: "100k",
      tierName: "Expert",
      platforms: ["mt5", "volumetrica"],
      challenge: [
        {
          variant: "single",
          presel: "expert-100k",
          data: {
            priceUsd: 445,
            profitTargetUsd: 8000,
            maxDrawdownUsd: 6000,
            maxPositionLossPct: 1,
            consistencyPct: null,
            durationDays: 30,
            extensionFeeUsd: 5,
          },
        },
      ],
      funded: [
        {
          variant: "single",
          presel: "expert-100k",
          data: {
            activationFeeUsd: 149,
            profitSplitTrader: 90,
            payoutEveryDays: 10,
            maxDrawdownUsd: 6000,
            maxPositionLossPct: 0.5,
            consistencyPct: 30,
            resetFeeUsd: 599,
            extensionFeeUsd: 5,
          },
        },
      ],
    },
    {
      size: "200k",
      tierName: "Master",
      platforms: ["mt5", "volumetrica"],
      challenge: [
        {
          variant: "single",
          presel: "master-200k",
          data: {
            priceUsd: 999,
            profitTargetUsd: 16_000,
            maxDrawdownUsd: 10_000,
            maxPositionLossPct: 1,
            consistencyPct: null,
            durationDays: 30,
            extensionFeeUsd: 5,
          },
        },
      ],
      funded: [
        {
          variant: "single",
          presel: "master-200k",
          data: {
            activationFeeUsd: 999,
            profitSplitTrader: 90,
            payoutEveryDays: 10,
            maxDrawdownUsd: 10_000,
            maxPositionLossPct: 0.5,
            consistencyPct: 30,
            resetFeeUsd: null,
            extensionFeeUsd: 5,
          },
        },
      ],
    },
  ],
};

const cfdRapidFunded = (size: "50k" | "100k"): FundedCol[] => {
  const mdd = size === "50k" ? 2000 : 3000;
  const presel = size === "50k" ? "rapid-advanced-50k" : "rapid-expert-100k";
  return [
    {
      // no Activation Fee row in the Rapid funded view (client feedback 2026-09-02)
      variant: "classic",
      presel,
      data: {
        profitSplitTrader: 80,
        payoutEveryDays: 3,
        maxDrawdownUsd: mdd,
        maxPositionLossPct: 0.5,
        consistencyPct: 40,
        resetFeeUsd: null,
        extensionFeeUsd: 5,
      },
    },
    {
      variant: "daily",
      presel,
      data: {
        profitSplitTrader: 80,
        payoutEveryDays: 1,
        maxDrawdownUsd: mdd,
        maxPositionLossPct: 0.5,
        consistencyPct: null,
        resetFeeUsd: null,
        extensionFeeUsd: 5,
      },
    },
  ];
};

const CFD_RAPID: Product = {
  id: "rapid",
  maxAccounts: 5,
  sizes: [
    {
      size: "50k",
      platforms: ["mt5", "volumetrica"],
      challenge: [
        {
          variant: "single",
          presel: "rapid-advanced-50k",
          data: {
            priceUsd: 675,
            profitTargetUsd: 3000,
            maxDrawdownUsd: 2000,
            maxPositionLossPct: 1,
            consistencyPct: 40,
            durationDays: 30,
            extensionFeeUsd: 5,
          },
        },
      ],
      funded: cfdRapidFunded("50k"),
    },
    {
      size: "100k",
      platforms: ["mt5", "volumetrica"],
      challenge: [
        {
          variant: "single",
          presel: "rapid-expert-100k",
          data: {
            priceUsd: 1125,
            profitTargetUsd: 6000,
            maxDrawdownUsd: 3000,
            maxPositionLossPct: 1,
            consistencyPct: 40,
            durationDays: 30,
            extensionFeeUsd: 5,
          },
        },
      ],
      funded: cfdRapidFunded("100k"),
    },
  ],
};

const CFD_INSTANT: Product = {
  id: "instant",
  maxAccounts: 10,
  sizes: [
    {
      size: "50k",
      platforms: ["mt5", "volumetrica"],
      challenge: [],
      funded: [
        {
          variant: "single",
          presel: "instant-funding-50k",
          priceUsd: 1745,
          // no Activation Fee row in the CFD Instant view
          data: {
            profitSplitTrader: 90,
            payoutEveryDays: 10,
            maxDrawdownUsd: 3000,
            maxPositionLossPct: 0.5,
            consistencyPct: 30,
            resetFeeUsd: 299,
            extensionFeeUsd: 5,
          },
        },
      ],
    },
    {
      size: "100k",
      platforms: ["mt5", "volumetrica"],
      challenge: [],
      funded: [
        {
          variant: "single",
          presel: "instant-funding-100k",
          priceUsd: 3495,
          // no Activation Fee row in the CFD Instant view
          data: {
            profitSplitTrader: 90,
            payoutEveryDays: 10,
            maxDrawdownUsd: 6000,
            maxPositionLossPct: 0.5,
            consistencyPct: 30,
            resetFeeUsd: 599,
            extensionFeeUsd: 5,
          },
        },
      ],
    },
  ],
};

/* ── §4.2 Futures ──────────────────────────────────────────────────────── */

const futCoreSize = (size: "50k" | "100k"): SizeEntry => {
  const big = size === "100k";
  return {
    size,
    platforms: ["dxfeed", "tradovate"],
    challenge: [
      {
        // variant A: Max Position Loss — dxFeed only
        variant: "mpl",
        presel: big ? "fut-challenge-100k" : "fut-challenge-50k",
        data: {
          priceUsd: big ? 475 : 179,
          profitTargetUsd: big ? 6000 : 3000,
          maxDrawdownUsd: big ? 6000 : 3000,
          maxPositionLossPct: 1,
          consistencyPct: null,
          durationDays: 30,
          extensionFeeUsd: 19,
        },
      },
      {
        // variant B: Daily Loss Limit — dxFeed + Tradovate
        variant: "dll",
        presel: big ? "fut-dll-challenge-100k" : "fut-dll-challenge-50k",
        data: {
          priceUsd: big ? 475 : 245,
          profitTargetUsd: big ? 6000 : 3000,
          maxDrawdownUsd: big ? 3000 : 2000,
          dailyLossLimitUsd: big ? 1500 : 1000,
          consistencyPct: null,
          durationDays: 30,
          extensionFeeUsd: 19,
        },
      },
    ],
    funded: [
      {
        variant: "mpl",
        presel: big ? "fut-challenge-100k" : "fut-challenge-50k",
        data: {
          activationFeeUsd: big ? 149 : 129,
          profitSplitTrader: 90,
          payoutEveryDays: 10,
          maxDrawdownUsd: big ? 6000 : 3000,
          maxPositionLossPct: 0.5,
          consistencyPct: 30,
          resetFeeUsd: big ? 599 : 299,
          extensionFeeUsd: 19,
        },
      },
      {
        variant: "dll",
        presel: big ? "fut-dll-challenge-100k" : "fut-dll-challenge-50k",
        data: {
          activationFeeUsd: big ? 149 : 129,
          profitSplitTrader: 90,
          payoutEveryDays: 10,
          maxDrawdownUsd: big ? 3000 : 2000,
          dailyLossLimitUsd: big ? 1500 : 1000,
          consistencyPct: 30,
          resetFeeUsd: big ? 599 : 299,
          extensionFeeUsd: 19,
        },
      },
    ],
  };
};

const futRapidSize = (size: "50k" | "100k"): SizeEntry => {
  const big = size === "100k";
  const presel = big ? "fut-rapid-challenge-100k" : "fut-rapid-challenge-50k";
  const mdd = big ? 3000 : 2000;
  return {
    size,
    platforms: ["dxfeed", "tradovate"],
    challenge: [
      {
        // no Max Position Loss / Daily Loss Limit in the Futures Rapid challenge
        variant: "single",
        presel,
        data: {
          priceUsd: big ? 1125 : 675,
          profitTargetUsd: big ? 6000 : 3000,
          maxDrawdownUsd: mdd,
          consistencyPct: 40,
          durationDays: 30,
          extensionFeeUsd: 19,
        },
      },
    ],
    funded: [
      {
        // no Activation Fee / Extension Fee rows in the Futures Rapid funded view
        variant: "classic",
        presel,
        data: {
          profitSplitTrader: 80,
          payoutEveryDays: 3,
          maxDrawdownUsd: mdd,
          consistencyPct: 40,
          resetFeeUsd: null,
        },
      },
      {
        variant: "daily",
        presel,
        data: {
          profitSplitTrader: 80,
          payoutEveryDays: 1,
          maxDrawdownUsd: mdd,
          dailyLossLimitUsd: big ? 1500 : 1000,
          consistencyPct: null,
          resetFeeUsd: null,
        },
      },
    ],
  };
};

const futInstantSize = (size: "50k" | "100k"): SizeEntry => {
  const big = size === "100k";
  return {
    size,
    platforms: ["dxfeed", "tradovate"],
    challenge: [],
    funded: [
      {
        variant: "mpl",
        presel: big ? "fut-instant-funding-100k" : "fut-instant-funding-50k",
        priceUsd: big ? 3495 : 1745,
        data: {
          profitSplitTrader: 90,
          payoutEveryDays: 10,
          maxDrawdownUsd: big ? 6000 : 3000,
          maxPositionLossPct: 0.5,
          consistencyPct: 30,
          resetFeeUsd: big ? 599 : 299,
          extensionFeeUsd: 19,
        },
      },
      {
        variant: "dll",
        presel: big ? "fut-dll-instant-funding-100k" : "fut-dll-instant-funding-50k",
        priceUsd: big ? 3495 : 1745,
        data: {
          profitSplitTrader: 90,
          payoutEveryDays: 10,
          maxDrawdownUsd: big ? 3000 : 2000,
          dailyLossLimitUsd: big ? 1500 : 1000,
          consistencyPct: 30,
          resetFeeUsd: big ? 599 : 299,
          extensionFeeUsd: 19,
        },
      },
    ],
  };
};

export const CATALOG: Catalog = {
  cfd_crypto: {
    core: CFD_CORE,
    rapid: CFD_RAPID,
    instant: CFD_INSTANT,
  },
  futures: {
    core: { id: "core", maxAccounts: 10, sizes: [futCoreSize("50k"), futCoreSize("100k")] },
    rapid: { id: "rapid", maxAccounts: 5, sizes: [futRapidSize("50k"), futRapidSize("100k")] },
    instant: { id: "instant", maxAccounts: 10, sizes: [futInstantSize("50k"), futInstantSize("100k")] },
  },
};

export const PRODUCT_ORDER: ProductId[] = ["core", "rapid", "instant"];

/* ── Platform → visibility filtering (spec §2.2) ───────────────────────── */

function variantVisibleOnPlatform(variant: VariantId, platform: PlatformId): boolean {
  // Max Position Loss is dxFeed-only; every other variant exists on both
  // platform families of its asset.
  if (variant === "mpl") return platform === "dxfeed";
  return true;
}

/** Per-column platform rule: an explicit Sanity list wins over the default. */
function colVisibleOnPlatform(col: { variant: VariantId; platforms?: string[] }, platform: PlatformId): boolean {
  if (col.platforms?.length) return col.platforms.includes(platform);
  return variantVisibleOnPlatform(col.variant, platform);
}

export interface VisibleSize {
  size: SizeId;
  tierName?: string;
  challenge: ChallengeCol[];
  funded: FundedCol[];
}

export type RapidFundedVariants = "both" | "classic" | "daily";

/**
 * The sizes/columns to render for (asset, product, platform, stage-agnostic)
 * in the given catalog. Applies: size-platform availability (MT5 hides CFD
 * Core 200K), variant filtering (Tradovate hides MPL), and the Rapid
 * funded-variant flag.
 */
export function visibleSizesIn(
  catalog: Catalog,
  asset: Asset,
  productId: ProductId,
  platform: PlatformId,
  rapidFundedVariants: RapidFundedVariants = RAPID_FUNDED_VARIANTS,
): VisibleSize[] {
  const product = catalog[asset][productId];
  return product.sizes
    .filter((s) => s.platforms.includes(platform))
    .map((s) => {
      let funded = s.funded.filter((col) => colVisibleOnPlatform(col, platform));
      if (productId === "rapid" && rapidFundedVariants !== "both") {
        funded = funded.filter((col) => col.variant === rapidFundedVariants);
      }
      return {
        size: s.size,
        tierName: s.tierName,
        challenge: s.challenge.filter((col) => colVisibleOnPlatform(col, platform)),
        funded,
      };
    });
}

/** Default-catalog convenience wrapper. */
export function visibleSizes(asset: Asset, productId: ProductId, platform: PlatformId): VisibleSize[] {
  return visibleSizesIn(CATALOG, asset, productId, platform, RAPID_FUNDED_VARIANTS);
}

/** Headline price for a funded column: Instant's own price, else its challenge price. */
export function fundedHeadlinePrice(sizeEntry: VisibleSize, col: FundedCol): number | undefined {
  if (col.priceUsd != null) return col.priceUsd;
  const challenge =
    sizeEntry.challenge.find((ch) => ch.variant === col.variant) ?? sizeEntry.challenge[0];
  return challenge?.data.priceUsd;
}
